import { CHECK, CLOSE } from "@apptractive/stm-shared";
import { DateTime } from "luxon";
import React, { useState, useMemo, useRef, useEffect } from "react";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  WBChipList,
  WBFlex,
  WBIconButton,
  WBImage,
  WBModal,
  WBNoResults,
  WBS3Card,
  WBTypography,
} from "@apptractive/design-system-web";
import { Box, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { optionsByGroup } from "../../graphql/queries";
import { Option, Product, ProductStatus, UserConversation } from "../../API";
import { PageContainer } from "../../components/PageContainer";
import { SelectOptionsForm } from "../../components/SelectOptionsForm";
import { SubscriptionStatusAlert } from "../../components/SubscriptionStatusAlert";
import {
  CScreateConversation as CREATE_CONVERSATION,
  CScreateUserConversation as CREATE_USER_CONVERSATION,
} from "../../customGraphql/mutations";
import {
  CSGetSub as GET_SUB,
  CSlistUserConversations as LIST_USER_CONVERSATIONS,
} from "../../customGraphql/queries";
import { updateUser as UPDATE_USER } from "../../graphql/mutations";
import {
  getSubscriptionStatus as GET_SUBSCRIPTION_STATUS,
  getUser as GET_USER,
  productsByStatus as LIST_PRODUCTS,
} from "../../graphql/queries";
import TinderCard from "react-tinder-card";
import ProductDetail from "../../components/ProductDetail";
import { getExpiry } from "../../helpers/datetime";

type Direction = "left" | "right" | "up" | "down";

const Swipes = () => {
  const { t } = useTranslation();
  const [nextToken, setNextToken] = useState<string | null>("");
  const [showFilters, setShowFilters] = useState(false);
  const [showProduct, setShowProduct] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const currentIndexRef = useRef(currentIndex);
  const [createConversation] = useMutation(gql(CREATE_CONVERSATION));
  const [createUserConversation] = useMutation(gql(CREATE_USER_CONVERSATION));
  const { data: subData } = useQuery(gql(GET_SUB));
  const { data: userData } = useQuery(gql(GET_USER), {
    variables: {
      id: subData?.sub,
    },
  });

  const { data: userConversationsData } = useQuery(
    gql(LIST_USER_CONVERSATIONS),
    {
      notifyOnNetworkStatusChange: true,
      skip: !subData?.sub,
      variables: {
        sortDirection: "DESC",
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const [updateUser, { loading: updateLoading, data: updateData }] =
    useMutation(gql(UPDATE_USER));

  const [listProducts, { data: productsData, loading: productsLoading }] =
    useLazyQuery(gql(LIST_PRODUCTS), {
      variables: {
        status: "ACTIVE",
        limit: 100,
        sortDirection: "DESC",
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
    });

  // set current index when new products are loaded
  useEffect(() => {
    if (products?.length > 0) {
      setCurrentIndex(products.length - 1);
      currentIndexRef.current = products.length - 1;
    }
  }, [products]);

  const cardRefs: any = useMemo(() => {
    if (products?.length > 0) {
      return Array(products.length)
        .fill(0)
        .map(() => React.createRef());
    }

    return [];
  }, [products]);

  const canSwipe = useMemo(() => currentIndex >= 0, [currentIndex]);
  const user = useMemo(
    () => updateData?.updateUser || userData?.getUser || {},
    [userData, updateData]
  );

  const { data: tagsData } = useQuery(gql(optionsByGroup), {
    notifyOnNetworkStatusChange: true,
    variables: {
      group: user?.country,
    },
    skip: !user?.country,
  });

  const tags = useMemo(
    () =>
      tagsData?.optionsByGroup?.items?.map((option: Option) => ({
        ...option,
        label: t(option.value, { ns: "options" }),
      })) || [],
    [tagsData, t]
  );

  const { data: locationsData } = useQuery(gql(optionsByGroup), {
    notifyOnNetworkStatusChange: true,
    variables: {
      group: `locations-${user?.country}`,
    },
    skip: !user?.country,
  });

  const locations = useMemo(
    () =>
      locationsData?.optionsByGroup?.items?.map((option: Option) => ({
        ...option,
        label: t(option.value, { ns: "options" }),
      })) || [],
    [locationsData, t]
  );

  const { data: subscriptionStatusData } = useQuery(
    gql(GET_SUBSCRIPTION_STATUS),
    {
      variables: {},
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    }
  );

  const subscriptionStatus = useMemo(
    () => subscriptionStatusData?.getSubscriptionStatus || {},
    [subscriptionStatusData]
  );

  // reset search if update to user interests
  useEffect(() => {
    if (
      user?.interests ||
      user?.locations ||
      user?.blocked ||
      user?.blockedBy
    ) {
      resetSearch();
    }
  }, [user.interests, user.locations, user.blocked, user.blockedBy]);

  useEffect(() => {
    const searchProducts = async () => {
      const interestsFilter = user?.interests?.map((interest: string) => ({
        tags: { contains: interest },
      }));

      const locationsFilter = user?.locations?.map((location: string) => ({
        locations: { contains: location },
      }));

      const variables: any = {
        skip: !user?.id,
        filter: {
          expiresAt: { gt: DateTime.now().toISO() },
          not: {
            owner: {
              eq: `${user?.owner}::${user?.owner}`,
            },
          },
          category: { eq: user.country },
        },
        status: ProductStatus.ACTIVE,
        nextToken: nextToken || null,
      };

      // filter by interests
      if (
        (interestsFilter?.length > 0 || locationsFilter?.length > 0) &&
        (tags?.length !== interestsFilter?.length ||
          locations?.length !== locationsFilter?.length)
      ) {
        variables.filter.and = {
          or: [],
        };

        if (
          interestsFilter?.length > 0 &&
          tags?.length !== interestsFilter?.length
        ) {
          // limitation by dynamodb - too many search filters - triggers Invalid FilterExpression: Expression size has exceeded the maximum allowed size; expression size: 4141
          variables.filter.and.or = [
            ...variables.filter.and.or,
            ...interestsFilter,
          ];
        }

        if (
          locationsFilter?.length > 0 &&
          locations?.length !== locationsFilter?.length
        ) {
          // limitation by dynamodb - too many search filters - triggers Invalid FilterExpression: Expression size has exceeded the maximum allowed size; expression size: 4141
          variables.filter.and.or = [
            ...variables.filter.and.or,
            ...locationsFilter,
          ];
        }
      }

      //// filter by locations
      //if (locationsFilter?.length > 0) {
      //  variables.filter.and = {
      //    or: locationsFilter,
      //  };
      //}

      const options = {
        variables,
      };

      let newProductsData;
      try {
        newProductsData = await listProducts(options);
      } catch (err) {
        console.log("ERROR list products: ", err);
      }

      if (newProductsData?.data?.productsByStatus) {
        const productIds =
          userConversationsData?.listUserConversations?.items?.map(
            ({ conversation }: UserConversation) => conversation?.productId
          );

        const filteredProducts = [
          ...newProductsData?.data?.productsByStatus?.items,
        ].filter(
          (product: Product) =>
            !productIds.includes(product.id) &&
            !user?.blocked?.includes(product.owner) &&
            !user?.blockedBy?.includes(product.owner)
        );

        const today = DateTime.now().toISODate();
        const refs = "SwipedProduct_" + today;
        const idsCache = (await localStorage.getItem(refs)) || undefined;

        // user has swiped left / no to any products yet, filter the results
        if (idsCache) {
          try {
            const ids = JSON.parse(idsCache);
            const unSwipedProducts = filteredProducts?.filter(
              (product: Product) => !ids.includes(product.id)
            );
            setProducts(unSwipedProducts);

            if (unSwipedProducts?.length === 0) {
              setNextToken(newProductsData?.data?.productsByStatus?.nextToken);
            }
          } catch (err) {
            console.log("ERROR parse idsCache", err);
          }
        }
        // user has not swiped left / no to any products yet
        else {
          setProducts(filteredProducts);

          if (filteredProducts?.length === 0) {
            setNextToken(newProductsData?.data?.productsByStatus?.nextToken);
          }
        }
      }
    };

    if (
      nextToken !== null &&
      user?.country &&
      currentIndex < 0 &&
      userConversationsData?.listUserConversations?.items &&
      !productsLoading
    ) {
      searchProducts();
    }
  }, [
    nextToken,
    user,
    currentIndex,
    userConversationsData,
    productsLoading,
    locations,
    tags,
  ]);

  // set last direction and decrease current index
  const swiped = (
    direction: Direction,
    nameToDelete: string,
    index: number
  ) => {
    if (index === 0 && productsData?.productsByStatus?.nextToken) {
      setNextToken(productsData?.productsByStatus?.nextToken);
    } else {
      setNextToken(null);
    }

    setCurrentIndex(index - 1);
    currentIndexRef.current = index - 1;
  };

  //const outOfFrame = (name: string, idx: number) => {
  //  console.log(`${name} (${idx}) left the screen!`, currentIndexRef.current);
  //  // handle the case in which go back is pressed before card goes outOfFrame
  //  // currentIndexRef.current >= idx && childRefs[idx].current.restoreCard();
  //  // when quickly swipe and restore multiple times the same card,
  //  // it happens multiple outOfFrame events are queued and the card disappear
  //  // during latest swipes. Only the last outOfFrame event should be considered valid
  //};

  const swipe = async (dir: Direction) => {
    if (
      canSwipe &&
      currentIndex < products.length &&
      cardRefs[currentIndex]?.current
    ) {
      cardRefs[currentIndex].current.swipe(dir); // Swipe the card!
      const product = { ...products[currentIndex] };

      if (dir === "right") {
        let conversationData;
        try {
          conversationData = await createConversation({
            variables: {
              input: {
                id: `${subData?.sub}-${product.id}`, // prevents duplicate matches
                title: product.title,
                country: product.country,
                productId: product.id,
                users: [subData?.sub, product.owner],
                readBy: [],
                createdBy: subData?.sub,
              },
            },
          });
        } catch (err) {
          console.log("ERROR create convo: ", err);
        }

        if (conversationData?.data?.createConversation?.id) {
          try {
            createUserConversation({
              variables: {
                input: {
                  conversationId: conversationData.data.createConversation.id,
                  userId: subData?.sub,
                  users: [subData?.sub, product.owner],
                },
              },
            });
          } catch (err) {
            console.log("ERROR create my user convo: ", err);
          }

          try {
            createUserConversation({
              variables: {
                input: {
                  conversationId: conversationData.data.createConversation.id,
                  userId: product.owner,
                  users: [subData?.sub, product.owner],
                  //userUserConversationsId: product.owner,
                  // owner: product.owner
                },
              },
            });
          } catch (err) {
            console.log("ERROR create other user convo: ", err);
          }
        }
      }

      // swiped no - cache id to prevent re-appearing
      else if (dir === "left") {
        if (product.id) {
          cacheSwipedLeftId(product.id);
        }
      }
    }
  };

  const cacheSwipedLeftId = async (id: string) => {
    const today = DateTime.now().toISODate();
    const refs = "SwipedProduct_" + today;
    const idsCache = (await localStorage.getItem(refs)) || undefined;

    let ids = [];
    if (idsCache) {
      try {
        ids = JSON.parse(idsCache);
        ids.push(id);
      } catch (err) {
        console.log("ERROR parse idsCache", err);
      }
    } else {
      ids.push(id);
    }

    localStorage.setItem(refs, JSON.stringify(ids));
  };

  const onInterestsUpdate = async (options: Option[]) => {
    try {
      await updateUser({
        variables: {
          input: {
            id: subData?.sub,
            interests: options.map((option) => option.value),
          },
        },
      });
    } catch (err) {
      console.log("err: ", err);
    }

    if (user?.country === "CH") {
      setShowFilters(false);
    }
  };

  const onLocationsUpdate = async (options: Option[]) => {
    try {
      await updateUser({
        variables: {
          input: {
            id: subData?.sub,
            locations: options.map((interest) => interest.value),
          },
        },
      });
    } catch (err) {
      console.log("err: ", err);
    }
  };

  const resetSearch = () => {
    setNextToken("");
    setProducts([]);
    setCurrentIndex(-1);
  };

  return (
    <>
      <WBModal
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title={t("searchFiltersTitle", { ns: "products" })}
        sx={{
          width: "95%",
          height: "95%",
        }}
      >
        <WBFlex flexDirection="column">
          <WBTypography variant="h5">
            {t("tagsTitle", { ns: "common" })}
          </WBTypography>

          <SelectOptionsForm
            selectedOptions={user?.interests}
            category={user?.country}
            onSubmit={onInterestsUpdate}
            loading={updateLoading}
          />

          {user?.country !== "CH" && (
            <>
              <WBTypography variant="h5">
                {t("locationsTitle", { ns: "common" })}
              </WBTypography>
              <SelectOptionsForm
                selectedOptions={user?.locations}
                category={`locations-${user?.country}`}
                onSubmit={onLocationsUpdate}
                loading={updateLoading}
              />
            </>
          )}
        </WBFlex>
      </WBModal>

      <WBModal
        open={!!showProduct}
        onClose={() => setShowProduct("")}
        sx={{ width: "95%", height: "95%" }}
      >
        <ProductDetail product={products?.[currentIndex]} />
      </WBModal>
      <PageContainer sx={{ flex: 1 }}>
        <WBFlex p={2}>
          <Box flex={1} />
          <Box flex={1} textAlign="right">
            <WBIconButton
              icon="Options"
              size="small"
              type="button"
              onClick={() => setShowFilters(true)}
            />
          </Box>
        </WBFlex>

        <SubscriptionStatusAlert
          remaining={subscriptionStatus?.monthlyAccessQuantity}
          titleRemaining={t("monthlyAccessRemaining", { ns: "purchase" })}
          titleUpgrade={t("upgradePlanAdditionalMonthlyAccess", {
            ns: "purchase",
          })}
          titleMaximum={t("maximumMonthlyAccess", { ns: "purchase" })}
          isOwner={subscriptionStatus?.isOwner}
          isExpired={subscriptionStatus?.isExpired}
          canUpgrade={subscriptionStatus?.canUpgrade}
          isBrand={false}
        />

        {subscriptionStatus?.isExpired !== true && (
          <WBFlex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            flex={1}
          >
            <WBFlex
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                position="relative"
                sx={{
                  width: "90vw",
                  height: {
                    xs: "390px",
                    sm: "500px",
                    md: "530px",
                  },
                  maxWidth: {
                    xs: "200px",
                    sm: "360px",
                    md: "400px",
                  },
                  display: canSwipe || productsLoading ? "block" : "none",
                }}
              >
                {products?.length === 0 && productsLoading && (
                  <Skeleton width="100%" height="100%" />
                )}
                {products?.map((product, index) => (
                  <Box key={product.id} position="absolute" width="100%">
                    <TinderCard
                      ref={cardRefs[index]}
                      onSwipe={(dir) => swiped(dir, product.title, index)}
                    >
                      <WBIconButton
                        onClick={() =>
                          setShowProduct(products?.[currentIndex]?.id)
                        }
                      >
                        <WBS3Card
                          image={product.images?.[0]}
                          title={product.title}
                          subTitle={
                            product.expiresAt
                              ? getExpiry(product.expiresAt)
                              : undefined
                          }
                          description={product.description}
                          additional={
                            <WBFlex alignItems="center" flexWrap="wrap">
                              <WBChipList
                                tags={product?.tags}
                                numTags={5}
                                color="secondary"
                                variant="filled"
                                sx={{
                                  background:
                                    "linear-gradient(135deg, #63b4f6, #78E9D3)",
                                  color: "#2C4052",
                                }}
                              />
                            </WBFlex>
                          }
                          sx={{
                            width: "90vw",
                            height: {
                              xs: "390px",
                              sm: "500px",
                              md: "530px",
                            },
                            maxWidth: {
                              xs: "200px",
                              sm: "360px",
                              md: "400px",
                            },
                          }}
                        />
                      </WBIconButton>
                    </TinderCard>
                  </Box>
                ))}
              </Box>
            </WBFlex>
            <WBFlex>
              {!canSwipe && !productsLoading && (
                <WBFlex justifyContent="center" my={3} p={3}>
                  <WBNoResults
                    title={t("noMoreProducts", { ns: "products" })}
                    description={
                      user?.interests?.length > 0
                        ? t("clearFiltersDescription", { ns: "products" })
                        : undefined
                    }
                    btnTitle={t("updateFiltersTitle", { ns: "products" })}
                    onClick={() => setShowFilters(true)}
                  />
                </WBFlex>
              )}
            </WBFlex>
            {canSwipe && (
              <WBFlex justifyContent="center" my={3} p={3}>
                <WBIconButton
                  sx={{
                    mr: 6,
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                  onClick={() => swipe("left")}
                >
                  <WBImage
                    src={CLOSE.default}
                    alt="Swipe no"
                    sx={{ maxWidth: "100px" }}
                  />
                </WBIconButton>
                <WBIconButton
                  sx={{
                    ml: 1,
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                  onClick={() => swipe("right")}
                >
                  <WBImage
                    src={CHECK.default}
                    alt="Swipe yes"
                    sx={{ maxWidth: "100px" }}
                  />
                </WBIconButton>
              </WBFlex>
            )}
          </WBFlex>
        )}
      </PageContainer>
    </>
  );
};

export default Swipes;
